import { useCallback, useReducer, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Page, CrudMode } from '@ohpen/enso-ui';
import {
  ExchangeRatesCrud,
  ExchangeRatesList,
  EXCHANGE_RATE_VALUE_LIST_NAMES,
  ExchangeRateDelete,
  ConfirmCrDeleteModal,
} from '@pmp/components';
import { ExchangeRateCrudRequest, exchangeRatesCreateDeleteCr, exchangeRatesValidateDelete } from '@pmp/api';
import { useValueListsStore } from '@pmp/stores';

import { initialState, reducer } from './reducer';
import { usePmpFetch } from '@pmp/hooks';

export const ExchangeRatesPage = (): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('exchangeRatesGetAllExchangeRates');
  const { valueLists, getValueLists } = useValueListsStore();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { t } = useTranslation();

  useEffect(() => {
    getValueLists({ fetchIfMissing: EXCHANGE_RATE_VALUE_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: ExchangeRateCrudRequest) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onCreateNewExchangeRate = useCallback((record: ExchangeRateCrudRequest) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDeleteExchangeRate = useCallback((record: ExchangeRateDelete) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  return (
    <Page>
      <Page.Title>{t('pmp.exchangeRates.title')}</Page.Title>
      <Page.Content>
        <ExchangeRatesList
          isFetching={isFetching}
          valueLists={valueLists}
          data={data ?? []}
          onCreate={onCreate}
          onView={onView}
          onCreateNewExchangeRate={onCreateNewExchangeRate}
          onDeleteExchangeRate={onDeleteExchangeRate}
        />
        {state.crud.open && (
          <ExchangeRatesCrud
            valueLists={valueLists}
            mode={state.crud.mode}
            record={state.crud.record}
            refreshTable={fetchData}
            onClose={onCloseCrud}
          />
        )}
        <ConfirmCrDeleteModal
          open={state.deleteModal.open}
          records={state.deleteModal.record ? [state.deleteModal.record] : []}
          idKey="id"
          nameKey="name"
          entityLabel={t('pmp.exchangeRates.labels.exchangeRate')}
          entityLabelPlural={t('pmp.exchangeRates.labels.exchangeRates')}
          getPrecheck={exchangeRatesValidateDelete}
          createDeleteChangeRequest={exchangeRatesCreateDeleteCr}
          refreshTable={fetchData}
          onClose={onCloseDeleteModal}
        />
      </Page.Content>
    </Page>
  );
};
